<template>
  <page-loader>
    <v-container>
      <v-row no-gutters>
        <v-col class="d-flex align-center justify-space-between">
          <v-spacer />

          <autocomplete-input
            v-model="query.params.offerType"
            :items="getItemsForOption('offerTypes')"
            label="Offer Type"
            clearable
            hide-details
          />

          <search-input
            v-model="query.searchTerm"
            :disabled="!canPerformActions"
            :is-loading="isLoading"
            class="ml-4"
            hide-details
          />
        </v-col>
      </v-row>
    </v-container>
    <v-container>
      <v-row>
        <v-col>
          <v-card outlined>
            <v-card-text>
              <v-simple-table height="500" dense fixed-header>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">Offer</th>
                      <th class="text-left">Aliases</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="mapping in mappedAliases" :key="mapping.slug">
                      <td>
                        <router-link :to="{ name: 'offers.edit', params: { slug: mapping.slug } }">
                          {{ mapping.name }}
                        </router-link>
                      </td>
                      <td>
                        <div v-for="alias in mapping.aliases" :key="alias">
                          <code>{{ alias }}</code>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <v-row>
        <v-spacer></v-spacer>
        <v-col cols="auto">
          <v-btn :disabled="isLoading" :loading="isLoading" color="primary" depressed @click="onClickExport">
            Export list as CSV
          </v-btn>
        </v-col>
      </v-row>
    </v-container>
  </page-loader>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'

import AutocompleteInput from '@/components/forms/autocomplete-input'
import PageLoader from '@/components/page-loader'
import SearchInput from '@/components/forms/search-input'

import PermissionsHandler from '@/mixins/permissions-handler'
import QueryParamsHandler from '@/mixins/query-params-handler'

import { offers, slugMap } from '@/services/offers'

import { exportCSV } from '@/utils/csv'

export default {
  name: 'AliasMap',
  components: {
    AutocompleteInput,
    PageLoader,
    SearchInput,
  },
  mixins: [PermissionsHandler, QueryParamsHandler],
  data: () => ({
    isLoading: false,
    defaultQuery: {
      offerType: 'crypto',
    },
    queriesDefined: ['offerType'],
    queryDefaults: ['offerType'],
    offers: [],
    aliases: [],
  }),
  computed: {
    ...mapGetters('options', ['formattedOption', 'getItemsForOption']),
    mappedAliases() {
      return this.offers
        .filter(({ type }) => !this.query.params.offerType || type === this.query.params.offerType)
        .map(({ slug, name, type }) => {
          const match = this.aliases.find(alias => alias.slug === slug) || { aliases: [] }
          return {
            slug,
            name: `${name} (${this.formattedOption('offerTypes', type)})`,
            aliases: match.aliases,
          }
        })
        .filter(({ aliases }) => aliases.length)
        .filter(
          ({ aliases, name }) =>
            !this.query.searchTerm ||
            aliases.some(
              alias => !this.query.searchTerm || alias.toLowerCase().includes(this.query.searchTerm.toLowerCase())
            ) ||
            name.toLowerCase().includes(this.query.searchTerm.toLowerCase())
        )
    },
  },
  async created() {
    await Promise.all([this.getOfferData(), this.getSlugMapData()])
    this.setPageLoading(false)
  },
  methods: {
    ...mapMutations('general', ['setPageLoading', 'setPageErrors']),
    async getOfferData() {
      this.isLoading = true

      try {
        const { data } = await offers.getAllOffers({ compact: true })
        this.offers = data
      } catch (error) {
        this.setPageErrors(true)
        this.$notify({
          type: 'error',
          text: 'Unable to fetch offer data',
          data: { error },
          duration: -1,
        })
      }

      this.isLoading = false
    },
    async getSlugMapData() {
      this.isLoading = true

      try {
        this.aliases = await slugMap.getAll()
      } catch (error) {
        this.setPageErrors(true)
        this.$notify({
          type: 'error',
          text: 'Unable to fetch alias data',
          data: { error },
          duration: -1,
        })
      }

      this.isLoading = false
    },
    onClickExport() {
      this.isLoading = true

      const headers = ['name', 'slug', 'alias'].join(',')
      const body = this.mappedAliases.map(({ name, slug, aliases }) =>
        aliases.map(alias => [name, slug, alias].join(',')).join('\n')
      )

      const csv = [headers].concat(body).join('\n')

      exportCSV(csv, 'alias-export')

      this.isLoading = false
    },
  },
}
</script>
